const BlockPaytableItem = require('./BlockPaytableItem');
const PSEvents = require('PSEvents');
const PSState = require('PSState');
const { GameStates } = require('ClientStates');

const State = cc.Enum({
    ACTIVE:  'ACTIVE',
    IDLE: 'IDLE',
    DISABLED: 'DISABLED'
});

const BlockPaytableController = cc.Class({
    extends: cc.Component,

    properties: {
        /**
         * Two prefabs, in case whether we change the design if the item is aligned left of right of the block
         * if no difference set the same prefab for both cases
         */
        paytableLeftItem: cc.Prefab,
        paytableRightItem: cc.Prefab,
        /**
         * button, where user can press and close paytable
         */
        panelButton: cc.Button,
        /**
         * Node, relative to which it will decide to put item left or right
         */
        panelRelativeNode: cc.Node,
        deltaX: 0,
        deltaY: 0,
        inputBlockTimeout: 0.5,
        overlay: cc.Node, // optional
        enableOnLoad: true
    },

    statics: {
        EVENT_BLOCK_CLICKED: 'blockInfoClicked'
    },

    start () {
        this.betChangeListener = (state) => this.updateBet(state);
        this.spinRequestListener = () => this.disable();
        this.roundEndedListener = () => this.idle();
        this.blockClickedListener = (block) => this.blockClicked(block);
        this.commonInitializedListener = () => this.init();
        this.menuOpened = () => {
            if (this.state === State.ACTIVE) {
                this._clean();
                this.idle();
            }
        };

        this.unblockInput = () => { this.isBlocked = false; };

        PSEvents.subscribe(PSEvents.COMMON_INITIALIZED, this.commonInitializedListener);
        PSEvents.subscribe(PSEvents.BET_CHANGED, this.betChangeListener);
        PSEvents.subscribe(PSEvents.SPIN_REQUEST, this.spinRequestListener);
        PSEvents.subscribe(PSEvents.ROUND_ANIMATION_ENDED, this.roundEndedListener);
        PSEvents.subscribe(BlockPaytableController.EVENT_BLOCK_CLICKED, this.blockClickedListener);
        PSEvents.subscribe(PSEvents.MENU_OPENED, this.menuOpened);

        if (PSEvents.getLast(PSEvents.COMMON_INITIALIZED)) {
            this.init();
        }

        /**
         * Instantiate left and right block paytable item
         */
        const leftNode = cc.instantiate(this.paytableLeftItem);
        leftNode.parent = this.node;
        leftNode.active = false;
        leftNode.zIndex = 2;
        this.leftItem = leftNode.getComponent(BlockPaytableItem);

        const rightNode = cc.instantiate(this.paytableRightItem);
        rightNode.parent = this.node;
        rightNode.active = false;
        rightNode.zIndex = 2;
        this.rightItem = rightNode.getComponent(BlockPaytableItem);

        if (!this.panelRelativeNode) {
            this.panelRelativeNode = this.node;
        }

        if (this.overlay) {
            this.overlayFadeTo = this.overlay.opacity;
        } else {
            this._showOverlay = () => {};
            this._hideOverlay = () => {};
        }
    },

    onDestroy () {
        PSEvents.unsubscribe(PSEvents.COMMON_INITIALIZED, this.commonInitializedListener);
        PSEvents.unsubscribe(PSEvents.BET_CHANGED, this.betChangeListener);
        PSEvents.unsubscribe(PSEvents.SPIN_REQUEST, this.spinRequestListener);
        PSEvents.unsubscribe(PSEvents.ROUND_ANIMATION_ENDED, this.roundEndedListener);
        PSEvents.unsubscribe(BlockPaytableController.EVENT_BLOCK_CLICKED, this.blockClickedListener);
        PSEvents.unsubscribe(PSEvents.MENU_OPENED, this.menuOpened);
    },

    init () {
        this.panelConfigurationPerState = PSState.gameConfiguration.panelConfigurationPerState;
        this.scaler = PSState.gameConfiguration.scaler;

        if (this.enableOnLoad) {
            this.idle();
        } else {
            this.disable();
        }
    },

    panelClick () {
        if (this.isBlocked) {
            return;
        }
        switch (this.state) {
            case State.ACTIVE:
                this._clean();
                this.idle();
                this.blockInput();
                break;
        }
    },

    blockClicked (block) {
        if (this.isBlocked || !this._isPayable(block.typeName)) {
            return;
        }
        switch (this.state) {
            case State.ACTIVE:
                this._clean();
                this.idle();
                this.blockInput();
                break;
            case State.IDLE:
                this.openPaytablePopup(block);
                this.blockInput();
                break;
            default:
                break;
        }
    },

    blockInput() {
        this.isBlocked = true;
        this.scheduleOnce(this.unblockInput, this.inputBlockTimeout);
    },

    disable () {
        if (this.state === State.DISABLED) {
            return;
        }
        if (this.state === State.ACTIVE) {
            this._clean();
        }
        this.state = State.DISABLED;
        this.panelButton.enabled = false;
    },

    roundEnded () {
        if (PSState.gameState === GameStates.MAIN) {
            this.idle();
        }
    },

    idle () {
        this.state = State.IDLE;
        this.panelButton.enabled = true;
    },

    updateBet ({ betAmount }) {
        if (this.betAmount != betAmount) {
            this.betAmount = betAmount;
            if (this.state === State.ACTIVE) {
                this.paytableItem.setPaytableInfo(this.paytableArray, this._getCoefficient());
            }
        }
    },

    openPaytablePopup (block) {
        this.node.emit('active');

        const bNode = block.node;

        this.state = State.ACTIVE;
        this.block = block;
        this.oldParent = bNode.parent;
        this.paytableArray = this._getPaytable()[block.typeName];

        this._showOverlay();

        const isAlignedLeft = this._isAlignedLeft(bNode);
        const sign = isAlignedLeft ? -1 : 1;

        bNode.setParentKeepPosition(this.node);

        if (block.showInfo) {
            block.showInfo();
        }

        this.paytableItem = isAlignedLeft ? this.leftItem : this.rightItem;
        this.paytableItem.setPaytableInfo(this.paytableArray, this._getCoefficient());
        this.paytableItem.node.active = true;
        this.paytableItem.node.x = bNode.position.x + (this.deltaX + bNode.width / 2) * sign;
        this.paytableItem.node.y = bNode.position.y + this.deltaY;
    },

    _isPayable (typeName) {
        const { paytable } = this.panelConfigurationPerState[PSState.gameState];

        return paytable.hasOwnProperty(typeName);
    },

    _getPaytable () {
        return this.panelConfigurationPerState[PSState.gameState].paytable;
    },

    _getCoefficient () {
        const { paylines, scaler } = this.panelConfigurationPerState[PSState.gameState];
        let coef = PSState.betAmount;

        if (scaler) {
            return coef / scaler;
        } else if (this.scaler) {
            return coef / this.scaler;
        } else if (paylines && paylines.length) {
            return coef / paylines.length;
        } else {
            return coef;
        }
    },

    _isAlignedLeft (bNode) {
        const wPos = this.oldParent.convertToWorldSpaceAR(bNode.position);
        const awPos = this.panelRelativeNode.parent.convertToWorldSpaceAR(this.panelRelativeNode.position);

        return wPos.x > awPos.x;
    },

    _clean () {
        if (!this.block) {
            return;
        }
        this._hideOverlay();
        this.block.node.setParentKeepPosition(this.oldParent);
        if (this.block.hideInfo) {
            this.block.hideInfo();
        }
        this.block = null;
        this.paytableItem.node.active = false;
        this.paytableItem = null;
    },

    _showOverlay () {
        const opacity = this.overlayFadeTo;

        this.overlay.opacity = 0;
        this.overlay.active = true;
        cc.Tween.stopAllByTarget(this.overlay);
        cc.tween(this.overlay).to(0.2, { opacity }).start();
    },

    _hideOverlay () {
        const opacity = 0;
        cc.Tween.stopAllByTarget(this.overlay);
        this.overlay.stopAllActions();
        cc.tween(this.overlay).to(0.2, { opacity }).call(() => {
            this.overlay.active = false;
        }).start();
    },
});
